import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { Link, useLocation } from 'react-router-dom'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import {
  ArrowRight,
  ArrowUpRight,
  ChevronDown,
  LogOut,
  Phone,
  Search,
  UserRound,
} from 'lucide-react'
import {
  BUDGET_LINKS,
  NAV_LINKS,
  PAGE_LINKS,
  SITE,
  type NavMenu,
} from '../../data/site'
import type { Vehicle } from '../../data/vehicles'
import { nameOf } from '../../data/vehicles'
import { useVehiclesQuery } from '../../hooks/useVehicleQueries'
import { assetUrl } from '../../lib/http'
import { pkr, shortPrice } from '../../utils/format'
import BrandSlider from './BrandSlider'
import { groupByBrand } from './brandGroups'
import {
  EASE,
  EASE_INOUT,
  magnetic,
  navAutoHide,
  navIntro,
  prefersReducedMotion,
} from '../../animations/gsapAnimations'
import { useFocusTrap } from '../../hooks/useFocusTrap'
import { useAccount } from '../../hooks/account-context'
import BrandMark from '../ui/BrandMark'
import Button from '../ui/Button'

/* The bar over everything: a marque on the left, six items across the
   middle — two of which open a panel — and on the right the two round
   controls the reference draws: search and one lime pill.

   It is transparent over the black hero and gains a frosted carbon ground
   once the page moves under it. GSAP fades that ground's opacity, which
   interpolates cleanly where a border and a blur would not. */

type Panel = NavMenu | 'search'

/* ---------- the marque ---------- */

function Wordmark({ onClick }: { onClick?: () => void }) {
  return (
    <Link
      to="/home"
      onClick={onClick}
      aria-label={`${SITE.name} ${SITE.suffix} — home`}
      className="group flex shrink-0 items-center gap-3"
    >
      <span aria-hidden="true" className="block size-11 shrink-0">
        <BrandMark
          className="size-full rounded-full object-cover transition duration-300 group-hover:drop-shadow-[0_0_10px_rgb(200_245_66/0.45)]"
        />
      </span>
      <span className="flex flex-col leading-none">
        <strong className="font-display text-[19px] font-bold tracking-[-0.03em] text-platinum">
          {SITE.name}
        </strong>
        <small className="mt-1 text-[9.5px] font-semibold tracking-[0.28em] text-lime/85 uppercase">
          {SITE.tagline}
        </small>
      </span>
    </Link>
  )
}

/* Which menu item is lit.

   One of them points at a section rather than a page (/home#cars), and
   NavLink cannot tell those apart — it matches on the path, so the Cars item
   would light up over the whole home page. It is resolved here instead: an
   anchor is active on its own section and on the car pages it leads to. */
function activeFor(to: string, pathname: string, hash: string) {
  const [path, fragment] = to.split('#')

  if (fragment) {
    return (
      (pathname === path && hash === `#${fragment}`) ||
      pathname.startsWith('/cars')
    )
  }

  /* Home is lit on the home page only while no section anchor is — the
     anchor's own item has that. */
  if (path === '/home') return pathname === '/home' && !hash

  return pathname.startsWith(path)
}

/* Pages is a drawer rather than one destination. It stays lit on any route
   listed inside that drawer, so a child page still reads as part of the
   navigation even though the bar itself points at About. */
function navActiveFor(
  link: (typeof NAV_LINKS)[number],
  pathname: string,
  hash: string,
) {
  if (activeFor(link.to, pathname, hash)) return true
  return (
    link.menu === 'pages' &&
    PAGE_LINKS.some((page) => {
      const [path, fragment] = page.to.split('#')
      return pathname === path && (!fragment || hash === `#${fragment}`)
    })
  )
}

/* ---------- the round controls ---------- */

function RoundButton({
  label,
  onClick,
  active,
  children,
}: {
  label: string
  onClick: () => void
  active: boolean
  children: ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      aria-expanded={active}
      className={`grid size-11 place-items-center rounded-full border transition duration-300 ${
        active
          ? 'border-lime/60 bg-lime/10 text-lime'
          : 'border-line bg-raised/70 text-platinum hover:border-lime/50 hover:text-lime'
      }`}
    >
      {children}
    </button>
  )
}

/* Who is signed in, at the right-hand end of the bar.

   Signed out it is the round control the reference draws, leading to the
   sign-in page. Signed in it opens a small panel: the four places a buyer's
   own records live, and the way out. It is deliberately not a sixth item in
   the primary nav — an account is not a section of the site, it is the
   person looking at it. */
function AccountControl({ onNavigate }: { onNavigate: () => void }) {
  const { status, session, signOut } = useAccount()
  const [open, setOpen] = useState(false)
  const root = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    const onPointerDown = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false)
    }

    document.addEventListener('keydown', onKeyDown)
    document.addEventListener('pointerdown', onPointerDown, true)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.removeEventListener('pointerdown', onPointerDown, true)
    }
  }, [open])

  /* While the stored token is still being checked this draws as the signed-out
     control rather than as nothing: a gap that fills in a moment later shifts
     the whole right-hand group sideways under the cursor. */
  if (status !== 'signed-in') {
    return (
      <Link
        to="/sign-in"
        onClick={onNavigate}
        aria-label="Sign in to your account"
        className="grid size-11 place-items-center rounded-full border border-line bg-raised/70 text-platinum transition duration-300 hover:border-lime/50 hover:text-lime"
      >
        <UserRound aria-hidden="true" strokeWidth={1.9} className="size-4.5" />
      </Link>
    )
  }

  const name = session?.user.fullName || session?.user.username || 'Account'
  const initials =
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((word) => word[0]?.toUpperCase() ?? '')
      .join('') || 'SM'

  return (
    <div ref={root} className="relative">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label={`Your account, ${name}`}
        className={`grid size-11 place-items-center rounded-full border text-[12px] font-bold tracking-wide transition duration-300 ${
          open
            ? 'border-lime/60 bg-lime/10 text-lime'
            : 'border-line bg-raised/70 text-platinum hover:border-lime/50 hover:text-lime'
        }`}
      >
        {initials}
      </button>

      {open ? (
        <div
          role="menu"
          aria-label="Your account"
          className="absolute right-0 z-50 mt-3 w-60 overflow-hidden rounded-2xl border border-line bg-ink/95 backdrop-blur-xl"
        >
          <div className="border-b border-line px-4 py-3">
            <p className="truncate text-[13.5px] font-semibold text-platinum">{name}</p>
            <p className="mt-0.5 truncate text-[11.5px] text-dim">{session?.user.username}</p>
          </div>

          <div className="p-1.5">
            {ACCOUNT_LINKS.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                role="menuitem"
                onClick={() => {
                  setOpen(false)
                  onNavigate()
                }}
                className="block rounded-xl px-3 py-2.5 text-[13.5px] font-medium text-steel transition-colors hover:bg-lime/8 hover:text-platinum"
              >
                {link.label}
              </Link>
            ))}

            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false)
                signOut()
              }}
              className="flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-left text-[13.5px] font-medium text-red-300 transition-colors hover:bg-red-500/10"
            >
              <LogOut aria-hidden="true" strokeWidth={2} className="size-4" />
              Sign out
            </button>
          </div>
        </div>
      ) : null}
    </div>
  )
}

const ACCOUNT_LINKS = [
  { to: '/account', label: 'Overview' },
  { to: '/account/bookings', label: 'My bookings' },
  { to: '/account/instalments', label: 'My instalments' },
  { to: '/account/cars', label: 'My cars' },
]

/* ---------- the panels ---------- */

/* The Cars panel: the whole floor as a slider of featured cards, one slide
   per company. Brands come from live stock so the menu never shows a marque
   nobody can buy. */
function CarsPanel({
  vehicles,
  loading,
  onNavigate,
}: {
  vehicles: Vehicle[]
  loading: boolean
  onNavigate: () => void
}) {
  const groups = useMemo(() => {
    const counts = new Map<string, number>()
    for (const vehicle of vehicles) {
      counts.set(vehicle.brand, (counts.get(vehicle.brand) ?? 0) + 1)
    }
    const brands = [...counts.entries()]
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .slice(0, 8)
      .map(([brand]) => brand)
    return groupByBrand(vehicles, brands)
  }, [vehicles])

  return (
    <BrandSlider
      groups={groups}
      loading={loading}
      total={vehicles.length}
      onNavigate={onNavigate}
    />
  )
}

function PagesPanel({ onNavigate }: { onNavigate: () => void }) {
  return (
    <ul className="grid gap-2 p-4 sm:grid-cols-2 lg:grid-cols-5 lg:p-5">
      {PAGE_LINKS.map((page) => (
        <li key={page.to}>
          <Link
            to={page.to}
            onClick={onNavigate}
            className="group/page flex h-full flex-col gap-4 rounded-2xl border border-transparent p-4 transition duration-300 hover:border-line hover:bg-raised"
          >
            <span
              aria-hidden="true"
              className="icon-ring size-10 transition duration-300 group-hover/page:bg-lime group-hover/page:text-void"
            >
              <ArrowUpRight strokeWidth={2} className="size-4" />
            </span>
            <span>
              <strong className="block font-display text-[15px] font-bold tracking-[-0.02em] text-platinum">
                {page.label}
              </strong>
              <span className="mt-1 block text-[12.5px] leading-relaxed text-steel">
                {page.blurb}
              </span>
            </span>
          </Link>
        </li>
      ))}
    </ul>
  )
}

/* Type a marque, a model or a year; the floor narrows as you go. */
function SearchPanel({
  vehicles,
  onNavigate,
}: {
  vehicles: Vehicle[]
  onNavigate: () => void
}) {
  const [query, setQuery] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  const needle = query.trim().toLowerCase()
  const matches = needle
    ? vehicles
        .filter((vehicle) =>
          `${nameOf(vehicle)} ${vehicle.year} ${vehicle.colour}`
            .toLowerCase()
            .includes(needle),
        )
        .slice(0, 6)
    : vehicles.slice(0, 4)

  return (
    <div className="p-3">
      <label className="flex items-center gap-3 rounded-full border border-line bg-raised px-4 py-3 focus-within:border-lime">
        <Search aria-hidden="true" strokeWidth={1.8} className="size-4 shrink-0 text-lime" />
        <input
          ref={inputRef}
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search the floor — Corolla, Sportage, 2026…"
          aria-label="Search cars"
          className="w-full bg-transparent text-[14px] text-platinum placeholder:text-dim focus:outline-none"
        />
      </label>

      <ul className="mt-2 flex flex-col">
        {matches.length === 0 ? (
          <li className="px-4 py-6 text-center text-[13px] text-dim">
            Nothing on the floor matches that — we can source it.
          </li>
        ) : (
          matches.map((vehicle) => (
            <li key={vehicle.id}>
              <Link
                to={`/cars/${vehicle.id}`}
                onClick={onNavigate}
                className="flex items-center gap-3 rounded-xl px-2 py-2 transition-colors hover:bg-lime/8"
              >
                <span className="block h-11 w-16 shrink-0 overflow-hidden rounded-lg bg-surface">
                  {vehicle.images[0] ? (
                    <img
                      src={assetUrl(vehicle.images[0])}
                      alt=""
                      loading="lazy"
                      className="size-full object-contain p-0.5"
                    />
                  ) : null}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[14px] font-medium text-platinum">
                    {nameOf(vehicle)}
                  </span>
                  <span className="block text-[12px] text-dim tabular-nums">
                    {vehicle.year}
                    {vehicle.colour ? ` · ${vehicle.colour}` : ''}
                  </span>
                </span>
                <span className="shrink-0 text-[13px] font-semibold text-lime tabular-nums">
                  {vehicle.instalment
                    ? `${pkr.format(vehicle.instalment)}/mo`
                    : vehicle.price > 0
                      ? shortPrice(vehicle.price)
                      : 'POA'}
                </span>
              </Link>
            </li>
          ))
        )}
      </ul>

      <Link
        to="/home#cars"
        onClick={onNavigate}
        className="mt-1 flex items-center justify-center gap-1.5 rounded-xl border-t border-line py-3 text-[13px] font-medium text-lime transition-colors hover:text-lime-lift"
      >
        Browse every car
        <ArrowRight aria-hidden="true" strokeWidth={2} className="size-3.5" />
      </Link>
    </div>
  )
}

/* ---------- the bar ---------- */

function Navbar() {
  const { pathname, hash } = useLocation()

  /* One panel at a time, and it belongs to the page it was opened on: any
     navigation closes it without an effect. */
  const [openPanel, setOpenPanel] = useState<{ panel: Panel; at: string } | null>(null)
  const panel = openPanel?.at === pathname + hash ? openPanel.panel : null
  const setPanel = (next: Panel | null) =>
    setOpenPanel(next ? { panel: next, at: pathname + hash } : null)

  /* The drawer, on a phone, works the same way. */
  const [openedAt, setOpenedAt] = useState<string | null>(null)
  const drawerOpen = openedAt === pathname

  const headerRef = useRef<HTMLElement>(null)
  const backdropRef = useRef<HTMLDivElement>(null)
  const drawerRef = useRef<HTMLDivElement>(null)
  const drawerTimeline = useRef<gsap.core.Timeline | null>(null)
  const ctaRef = useRef<HTMLDivElement>(null)
  const leaveTimer = useRef<number | null>(null)

  useFocusTrap(drawerRef, drawerOpen)

  /* The catalogue, once: the mega menu and the search both read from it.
     Sold cars are not stock, and the API is asked not to send them rather
     than the browser being left to drop them — which is also the query the
     listing section asks for, so the two share one request. */
  const { data: floor = [], isPending: loading } = useVehiclesQuery({ unsold: true })

  /* The one magnetic element in the chrome: the primary action leans toward
     the cursor. Anything more and the bar starts performing. */
  useLayoutEffect(() => {
    const cta = ctaRef.current
    if (!cta) return
    return magnetic(cta, 0.28)
  }, [])

  /* --- arriving, and getting out of the way -----------------------------
     The bar's three groups drop into place once, on first load. After that
     it tucks itself away on a long scroll down and comes straight back on
     the first scroll up — unless a panel or the drawer is open, in which
     case it stays exactly where the pointer left it. The open state is read
     off the DOM rather than closed over, so the trigger is built once. */
  useLayoutEffect(() => {
    const header = headerRef.current
    if (!header) return

    const ctx = gsap.context(() => {
      const bar = header.querySelector('[data-nav="bar"]')
      if (bar) navIntro(bar)

      navAutoHide(
        header,
        () => header.querySelector('[aria-expanded="true"]') !== null,
      )
    }, header)

    return () => ctx.revert()
  }, [])

  /* --- the backdrop, weighted by scroll position ------------------------
     Transparent over the black hero — the reference sits its bar straight on
     the page — and frosted carbon once the page is actually scrolling under
     it, or whenever a panel is open and needs a ground to hang from. */
  useLayoutEffect(() => {
    const backdrop = backdropRef.current
    if (!backdrop) return

    const duration = prefersReducedMotion() ? 0 : 0.4

    gsap.set(backdrop, { opacity: window.scrollY > 80 ? 1 : 0 })

    const ctx = gsap.context(() => {
      const fade = (opacity: number) =>
        gsap.to(backdrop, { opacity, duration, ease: EASE_INOUT, overwrite: 'auto' })

      ScrollTrigger.create({
        start: 80,
        onEnter: () => fade(1),
        onLeaveBack: () => fade(0),
      })
    })

    return () => ctx.revert()
  }, [])

  /* --- the drawer timeline, built once ---------------------------------- */
  useLayoutEffect(() => {
    const drawer = drawerRef.current
    if (!drawer) return

    const ctx = gsap.context(() => {
      const timeline = gsap.timeline({ paused: true })

      if (prefersReducedMotion()) {
        timeline.set(drawer, { opacity: 1 })
      } else {
        timeline
          .fromTo(
            drawer,
            { opacity: 0, yPercent: -3 },
            { opacity: 1, yPercent: 0, duration: 0.5, ease: EASE },
          )
          .fromTo(
            drawer.querySelectorAll('[data-menu-item]'),
            { opacity: 0, y: 18 },
            { opacity: 1, y: 0, duration: 0.5, stagger: 0.05, ease: EASE },
            '-=0.3',
          )
      }

      drawerTimeline.current = timeline
    }, drawer)

    return () => {
      drawerTimeline.current = null
      ctx.revert()
    }
  }, [])

  useLayoutEffect(() => {
    const drawer = drawerRef.current
    const timeline = drawerTimeline.current
    if (!drawer || !timeline) return

    if (drawerOpen) {
      gsap.set(drawer, { visibility: 'visible' })
      timeline.play()
      return
    }

    const hide = () => gsap.set(drawer, { visibility: 'hidden' })

    if (timeline.time() === 0) {
      hide()
    } else {
      timeline.eventCallback('onReverseComplete', hide)
      timeline.reverse()
    }
  }, [drawerOpen])

  useEffect(() => {
    document.body.style.overflow = drawerOpen ? 'hidden' : ''
    return () => {
      document.body.style.overflow = ''
    }
  }, [drawerOpen])

  useEffect(() => {
    if (!drawerOpen && !panel) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpenedAt(null)
        setOpenPanel(null)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [drawerOpen, panel])

  /* A click anywhere outside the header closes whatever panel is open. */
  useEffect(() => {
    if (!panel) return
    const onPointerDown = (event: PointerEvent) => {
      if (!(event.target as Element).closest('[data-navbar]')) setOpenPanel(null)
    }
    document.addEventListener('pointerdown', onPointerDown)
    return () => document.removeEventListener('pointerdown', onPointerDown)
  }, [panel])

  const closeAll = () => {
    setOpenedAt(null)
    setOpenPanel(null)
  }

  /* Hover opens a menu and leaving the whole bar closes it — after a beat, so
     the pointer can cross the gap between the item and its panel. */
  const cancelLeave = () => {
    if (leaveTimer.current) window.clearTimeout(leaveTimer.current)
    leaveTimer.current = null
  }
  const scheduleLeave = () => {
    cancelLeave()
    leaveTimer.current = window.setTimeout(() => {
      setOpenPanel((current) =>
        current && (current.panel === 'cars' || current.panel === 'pages') ? null : current,
      )
    }, 160)
  }

  /* Tapping the section we are already on is not a navigation — the location
     does not change, so nothing would move the page. That one case is scrolled
     by hand, and smoothly, because the visitor is watching it happen. Every
     other case is a location change and ScrollToTop has it. */
  const onNavClick = (to: string) => {
    const [path, fragment] = to.split('#')
    if (!fragment || pathname !== path || hash !== `#${fragment}`) return

    document.getElementById(fragment)?.scrollIntoView({
      behavior: prefersReducedMotion() ? 'instant' : 'smooth',
      block: 'start',
    })
  }

  return (
    <header
      ref={headerRef}
      data-navbar
      className="fixed inset-x-0 top-0 z-50"
      onMouseEnter={cancelLeave}
      onMouseLeave={scheduleLeave}
    >
      {/* The frosted ground: absent over the hero, present once scrolled or
          while a panel is open. */}
      <div
        ref={backdropRef}
        aria-hidden="true"
        className="absolute inset-0 border-b border-line/70 bg-ink/85 backdrop-blur-xl"
      />
      {panel ? (
        <div
          aria-hidden="true"
          className="absolute inset-0 border-b border-line/70 bg-ink/85 backdrop-blur-xl"
        />
      ) : null}

      <div
        data-nav="bar"
        className="relative mx-auto flex h-[84px] w-full max-w-[1320px] items-center justify-between gap-6 px-5 sm:px-8"
      >
        <Wordmark onClick={closeAll} />

        <nav aria-label="Primary" className="hidden items-center gap-5 lg:flex xl:gap-8">
          {NAV_LINKS.map((link) => {
            const active = navActiveFor(link, pathname, hash)
            const menuOpen = link.menu !== undefined && panel === link.menu

            return (
              <div
                key={link.to}
                className="relative"
                onMouseEnter={() => {
                  cancelLeave()
                  if (link.menu) setPanel(link.menu)
                  else if (panel === 'cars' || panel === 'pages') setPanel(null)
                }}
              >
                <Link
                  to={link.to}
                  onClick={(event) => {
                    if (link.menu) {
                      /* The item is a switch for its panel — the panel's own
                         foot carries the link to the page itself. */
                      event.preventDefault()
                      setPanel(menuOpen ? null : link.menu)
                      return
                    }
                    setPanel(null)
                    onNavClick(link.to)
                  }}
                  onFocus={() => link.menu && setPanel(link.menu)}
                  aria-current={active ? 'page' : undefined}
                  aria-expanded={link.menu ? menuOpen : undefined}
                  aria-haspopup={link.menu ? 'true' : undefined}
                  className={`relative flex items-center gap-1 py-2 text-[14.5px] font-medium tracking-[-0.01em] transition-colors duration-200 after:absolute after:-bottom-1 after:left-1/2 after:size-1.5 after:-translate-x-1/2 after:rounded-full after:bg-lime after:transition after:duration-300 hover:text-platinum ${
                    active || menuOpen
                      ? 'text-platinum after:scale-100 after:opacity-100'
                      : 'text-steel after:scale-0 after:opacity-0'
                  }`}
                >
                  {link.label}
                  {link.menu ? (
                    <ChevronDown
                      aria-hidden="true"
                      strokeWidth={2}
                      className={`size-3.5 transition-transform duration-300 ${
                        menuOpen ? 'rotate-180 text-lime' : ''
                      }`}
                    />
                  ) : null}
                </Link>
              </div>
            )
          })}
        </nav>

        <div className="flex items-center gap-2.5 sm:gap-3">
          <div className="relative hidden md:block">
            <RoundButton
              label="Search cars"
              active={panel === 'search'}
              onClick={() => setPanel(panel === 'search' ? null : 'search')}
            >
              <Search aria-hidden="true" strokeWidth={1.9} className="size-4.5" />
            </RoundButton>
          </div>

          <AccountControl onNavigate={closeAll} />

          <div ref={ctaRef} className="hidden sm:block">
            <Button to="/home#cars" size="md" arrow onClick={() => onNavClick('/home#cars')}>
              Explore Now
            </Button>
          </div>

          <button
            type="button"
            onClick={() => setOpenedAt(drawerOpen ? null : pathname)}
            aria-expanded={drawerOpen}
            aria-controls="mobile-nav"
            aria-label={drawerOpen ? 'Close menu' : 'Open menu'}
            className="grid size-11 place-items-center rounded-full border border-line bg-raised/70 text-platinum transition hover:border-lime/50 lg:hidden"
          >
            <span className="relative block h-3 w-4.5">
              <span
                className={`absolute left-0 block h-px w-full bg-current transition duration-300 ${
                  drawerOpen ? 'top-1.5 rotate-45' : 'top-0'
                }`}
              />
              <span
                className={`absolute top-1.5 left-0 block h-px w-full bg-current transition duration-200 ${
                  drawerOpen ? 'opacity-0' : 'opacity-100'
                }`}
              />
              <span
                className={`absolute left-0 block h-px w-full bg-current transition duration-300 ${
                  drawerOpen ? 'top-1.5 -rotate-45' : 'top-3'
                }`}
              />
            </span>
          </button>
        </div>
      </div>

      {/* ---------- the panels, hanging from the bar ---------- */}
      {panel === 'cars' || panel === 'pages' ? (
        <div className="absolute inset-x-0 top-full hidden pt-2 lg:block">
          <div className="mx-auto w-full max-w-[1320px] px-5 sm:px-8">
            <div className="animate-rise overflow-hidden rounded-3xl border border-line bg-elevated/95 shadow-pop backdrop-blur-xl [animation-duration:0.45s]">
              {panel === 'cars' ? (
                <CarsPanel vehicles={floor} loading={loading} onNavigate={closeAll} />
              ) : (
                <PagesPanel onNavigate={closeAll} />
              )}
            </div>
          </div>
        </div>
      ) : null}

      {panel === 'search' ? (
        <div className="absolute inset-x-0 top-full hidden pt-2 md:block">
          <div className="mx-auto flex w-full max-w-[1320px] justify-end px-5 sm:px-8">
            <div className="w-full max-w-[420px] animate-rise overflow-hidden rounded-3xl border border-line bg-elevated/95 shadow-pop backdrop-blur-xl [animation-duration:0.4s]">
              <SearchPanel vehicles={floor} onNavigate={closeAll} />
            </div>
          </div>
        </div>
      ) : null}

      {/* ---------- the drawer, on a phone ----------
          Kept mounted so the timeline can play in both directions; visibility
          is toggled around the tween. */}
      <div
        id="mobile-nav"
        ref={drawerRef}
        aria-hidden={!drawerOpen}
        className="invisible fixed inset-x-0 top-[84px] bottom-0 z-40 overflow-y-auto bg-ink lg:hidden"
      >
        <nav
          aria-label="Mobile"
          className="mx-auto flex min-h-full w-full max-w-[1320px] flex-col px-5 pt-4 pb-12 sm:px-8"
        >
          {NAV_LINKS.map((link) => (
            <div key={link.to} data-menu-item className="border-b border-line-soft py-4">
              <Link
                to={link.to}
                tabIndex={drawerOpen ? 0 : -1}
                onClick={() => {
                  closeAll()
                  onNavClick(link.to)
                }}
                aria-current={navActiveFor(link, pathname, hash) ? 'page' : undefined}
                className={`flex items-center justify-between font-display text-[26px] font-bold tracking-[-0.03em] transition-colors ${
                  navActiveFor(link, pathname, hash) ? 'text-lime' : 'text-platinum'
                }`}
              >
                {link.label}
                <ArrowUpRight aria-hidden="true" strokeWidth={2} className="size-5 text-dim" />
              </Link>

              {link.menu === 'cars' ? (
                <ul className="mt-3 flex flex-wrap gap-2">
                  {[
                    { to: '/home?sort=newest#cars', label: 'New arrivals' },
                    { to: '/home?payment=lease#cars', label: 'On lease' },
                    ...BUDGET_LINKS.map((band) => {
                      const params = new URLSearchParams()
                      if ('min' in band && band.min) params.set('minPrice', String(band.min))
                      if ('max' in band && band.max) params.set('maxPrice', String(band.max))
                      return { to: `/home?${params}#cars`, label: band.label }
                    }),
                  ].map((chip) => (
                    <li key={chip.label}>
                      <Link
                        to={chip.to}
                        tabIndex={drawerOpen ? 0 : -1}
                        onClick={closeAll}
                        className="inline-block rounded-full border border-line px-3.5 py-1.5 text-[12.5px] text-silver transition hover:border-lime/50 hover:text-lime"
                      >
                        {chip.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              ) : null}

              {link.menu === 'pages' ? (
                <ul className="mt-3 flex flex-col gap-2.5">
                  {PAGE_LINKS.map((page) => (
                    <li key={page.to}>
                      <Link
                        to={page.to}
                        tabIndex={drawerOpen ? 0 : -1}
                        onClick={closeAll}
                        className="text-[14.5px] text-steel transition-colors hover:text-lime"
                      >
                        {page.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          ))}

          <div className="mt-auto flex flex-col gap-3 pt-10">
            <Button
              to="/home#cars"
              size="lg"
              arrow
              tabIndex={drawerOpen ? 0 : -1}
              onClick={closeAll}
              className="w-full"
            >
              Explore the floor
            </Button>
            <a
              href={SITE.phoneHref}
              data-menu-item
              tabIndex={drawerOpen ? 0 : -1}
              className="flex h-14 items-center justify-center gap-2 rounded-full border border-lime/55 text-[14.5px] font-semibold text-platinum"
            >
              <Phone aria-hidden="true" strokeWidth={1.8} className="size-4 text-lime" />
              Call {SITE.phone}
            </a>
          </div>
        </nav>
      </div>
    </header>
  )
}

export default Navbar
